import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Inbox, Mail, Building2, Trash2, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, handled: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/admin/contacts"), api.get("/admin/stats")]);
      setContacts(c.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleStatus = async (c) => {
    const next = c.status === "new" ? "handled" : "new";
    try {
      await api.patch(`/admin/contacts/${c.id}?status=${next}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la demande de ${c.name} ?`)) return;
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      toast.success("Demande supprimée");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const filtered = contacts.filter((c) => (filter === "all" ? true : c.status === filter));

  const statCards = [
    { label: "Total", value: stats.total, icon: Inbox },
    { label: "Nouvelles", value: stats.new, icon: Mail },
    { label: "Traitées", value: stats.handled, icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen grain relative">
      <header className="border-b border-border sticky top-0 bg-black/70 backdrop-blur-2xl z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-heading font-black text-lg tracking-tighter">GMP<span className="text-accent">.</span></span>
            <nav className="flex items-center gap-4 text-sm">
              <Link to="/admin" className="text-warm font-medium">Demandes</Link>
              <Link to="/admin/blog" className="text-muted-foreground hover:text-foreground transition-colors">Articles</Link>
              <Link to="/admin/cockpit" className="text-muted-foreground hover:text-foreground transition-colors">Cockpit Avis</Link>
            </nav>
          </div>
          <button
            onClick={doLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10 relative z-10">
        <h1 className="font-heading font-bold text-2xl sm:text-3xl mb-8">Demandes de contact</h1>

        <div className="grid grid-cols-3 gap-4 mb-10">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} className="bg-surface border border-border p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{s.label}</span>
                  <Icon size={16} className="text-accent" />
                </div>
                <p className="font-heading font-black text-3xl">{s.value}</p>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 mb-6">
          {["all", "new", "handled"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border transition-colors ${
                filter === f ? "border-accent text-accent bg-accent/5" : "border-border text-muted-foreground hover:text-white"
              }`}
            >
              {f === "all" ? "Toutes" : f === "new" ? "Nouvelles" : "Traitées"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-24 border border-border bg-surface">
            <Inbox size={40} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Aucune demande pour le moment.</p>
          </div>
        ) : (
          <div className="border border-border bg-surface divide-y divide-border" data-testid="contacts-list">
            {filtered.map((c) => (
              <div key={c.id} data-testid={`contact-row-${c.id}`} className="p-5 flex flex-col lg:flex-row lg:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <span className="font-semibold text-white">{c.name}</span>
                    {c.status === "new" ? (
                      <span className="font-mono text-[9px] tracking-wider uppercase bg-accent/15 text-accent px-2 py-0.5">Nouveau</span>
                    ) : (
                      <span className="font-mono text-[9px] tracking-wider uppercase bg-white/10 text-muted-foreground px-2 py-0.5">Traité</span>
                    )}
                    {c.interest && (
                      <span className="font-mono text-[9px] tracking-wider uppercase border border-border text-muted-foreground px-2 py-0.5">{c.interest}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3 flex-wrap">
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-accent transition-colors">
                      <Mail size={12} /> {c.email}
                    </a>
                    {c.company && (
                      <span className="flex items-center gap-1.5"><Building2 size={12} /> {c.company}</span>
                    )}
                    <span>{new Date(c.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{c.message}</p>
                </div>
                <div className="flex lg:flex-col gap-2 shrink-0">
                  <button
                    onClick={() => toggleStatus(c)}
                    data-testid={`toggle-status-${c.id}`}
                    className="flex items-center gap-1.5 text-xs border border-border px-3 py-2 hover:border-accent/50 hover:text-accent transition-colors"
                  >
                    {c.status === "new" ? <><CheckCircle2 size={14} /> Traiter</> : <><RotateCcw size={14} /> Rouvrir</>}
                  </button>
                  <button
                    onClick={() => remove(c)}
                    data-testid={`delete-contact-${c.id}`}
                    className="flex items-center gap-1.5 text-xs border border-border px-3 py-2 hover:border-destructive/60 hover:text-destructive transition-colors"
                  >
                    <Trash2 size={14} /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
